/**
 * The Control Center catalog.
 *
 * **Every row here works.** The page began as a fixture — thirty-six invented
 * rows, most of them opening a modal that said the form was not built — because
 * the surface was worth settling before the actions were wired. BOS-181 ended
 * that: an action is listed only if pressing it opens its real form or lands on
 * the screen that *is* that action, and `controlCenterSurfaces.ts` holds which.
 * A test asserts the invariant, because the alternative is auditing forty-five
 * rows by hand again.
 *
 * What that costs is the roadmap. Rows for things the product does not do —
 * banking, RS.GE, backup — were a map of where it was going, and the map is now
 * elsewhere. A launcher is judged by what happens when you press something.
 *
 * Names live in `pages.controlCenter.actions.*`, not in this file: an action is
 * user-facing copy in two languages, and the catalog is its structure.
 */

/** The eight sections the catalog is grouped under, in display order. */
export type ActionArea =
    | 'sales'
    | 'purchases'
    | 'branchesAndProducts'
    | 'bankAndPayments'
    | 'rsGe'
    | 'finance'
    | 'accounting'
    | 'peopleAndSystem';

/**
 * The icon-chip tint a pinned tile carries.
 *
 * Three values, from the DS semantic set — **not one hue per action**. Colour
 * here marks the six things someone chose to keep at hand; thirty-six tints
 * would mark nothing. Catalog rows carry no tint at all.
 */
export type ActionTint = 'accent' | 'success' | 'danger';

export type ControlAction = {
    /** Stable id: the i18n key suffix, the test-id discriminator, the pin key. */
    id: string;
    area: ActionArea;
    icon: string;
    /**
     * Whether the action navigates to its own screen (the arrow glyph) rather
     * than opening the modal in place. Stated once in the catalog header, so
     * the row needs one glyph and no legend of its own.
     */
    opensOwnPage: boolean;
    tint: ActionTint;
};

export const AREA_ORDER: ActionArea[] = [
    'sales',
    'purchases',
    'branchesAndProducts',
    'bankAndPayments',
    'rsGe',
    'finance',
    'accounting',
    'peopleAndSystem',
];

/** Only the compact catalog uses these — one glyph per collapsed area row. */
export const AREA_ICON: Record<ActionArea, string> = {
    sales: 'solar:chart-square-linear',
    purchases: 'solar:cart-large-2-linear',
    branchesAndProducts: 'solar:folder-with-files-linear',
    bankAndPayments: 'solar:card-linear',
    rsGe: 'solar:document-text-linear',
    finance: 'solar:wallet-linear',
    accounting: 'solar:notebook-square-linear',
    peopleAndSystem: 'solar:users-group-rounded-linear',
};

export const CONTROL_ACTIONS: ControlAction[] = [
    // Purchases
    {
        id: 'addSupplier',
        area: 'purchases',
        icon: 'solar:delivery-linear',
        opensOwnPage: false,
        tint: 'accent',
    },
    {
        id: 'newPurchase',
        area: 'purchases',
        icon: 'solar:cart-large-2-linear',
        opensOwnPage: true,
        tint: 'success',
    },
    {
        id: 'supplierList',
        area: 'purchases',
        icon: 'solar:users-group-two-rounded-linear',
        opensOwnPage: true,
        tint: 'accent',
    },
    // Branches and products
    {
        id: 'addProduct',
        area: 'branchesAndProducts',
        icon: 'solar:box-linear',
        opensOwnPage: true,
        tint: 'accent',
    },
    {
        // Shipped long ago, and listed as a Q2 roadmap item until BOS-181:
        // the products page is in the sidebar.
        id: 'productList',
        area: 'branchesAndProducts',
        icon: 'solar:box-linear',
        opensOwnPage: true,
        tint: 'accent',
    },
    {
        id: 'addCategory',
        area: 'branchesAndProducts',
        icon: 'solar:widget-2-linear',
        opensOwnPage: false,
        tint: 'accent',
    },
    {
        id: 'addBranch',
        area: 'branchesAndProducts',
        icon: 'solar:shop-2-linear',
        opensOwnPage: false,
        tint: 'accent',
    },
    {
        id: 'addBrand',
        area: 'branchesAndProducts',
        icon: 'solar:bookmark-linear',
        opensOwnPage: false,
        tint: 'accent',
    },
    {
        id: 'addAttributeType',
        area: 'branchesAndProducts',
        icon: 'solar:tag-linear',
        opensOwnPage: false,
        tint: 'accent',
    },
    // Finance
    {
        id: 'financialPosition',
        area: 'finance',
        icon: 'solar:chart-2-linear',
        opensOwnPage: true,
        tint: 'accent',
    },
    // Accounting — the three drawers open in place, like the other catalogue
    // forms; everything else navigates to its own screen, since acting on it
    // needs the full page (filters, tabs, the register itself).
    {
        id: 'addAccount',
        area: 'accounting',
        icon: 'solar:book-2-linear',
        opensOwnPage: false,
        tint: 'accent',
    },
    {
        id: 'addAsset',
        area: 'accounting',
        icon: 'solar:box-minimalistic-linear',
        opensOwnPage: false,
        tint: 'accent',
    },
    {
        id: 'addShareholder',
        area: 'accounting',
        icon: 'solar:user-circle-linear',
        opensOwnPage: false,
        tint: 'accent',
    },
    {
        id: 'accountingJournal',
        area: 'accounting',
        icon: 'solar:notebook-linear',
        opensOwnPage: true,
        tint: 'accent',
    },
    {
        id: 'accountingTrialBalance',
        area: 'accounting',
        icon: 'solar:clipboard-check-linear',
        opensOwnPage: true,
        tint: 'accent',
    },
    {
        id: 'accountingVat',
        area: 'accounting',
        // `solar:receipt-linear` does not exist in the Solar set. Iconify
        // resolves names over the network at runtime and renders nothing for
        // one it cannot find — no warning, no fallback glyph — so this row sat
        // iconless until somebody looked (BOS-184).
        icon: 'solar:bill-check-linear',
        opensOwnPage: true,
        tint: 'accent',
    },
    {
        id: 'recordOperation',
        area: 'accounting',
        icon: 'solar:pen-new-square-linear',
        opensOwnPage: true,
        tint: 'accent',
    },
    {
        id: 'operationsActivity',
        area: 'accounting',
        icon: 'solar:checklist-minimalistic-linear',
        opensOwnPage: true,
        tint: 'accent',
    },
    {
        id: 'operationTemplates',
        area: 'accounting',
        icon: 'solar:documents-linear',
        opensOwnPage: true,
        tint: 'accent',
    },
    {
        id: 'operationCategories',
        area: 'accounting',
        icon: 'solar:widget-2-linear',
        opensOwnPage: true,
        tint: 'accent',
    },
    {
        id: 'balanceSheet',
        area: 'accounting',
        icon: 'solar:scale-linear',
        opensOwnPage: true,
        tint: 'accent',
    },
    {
        id: 'profitLoss',
        area: 'accounting',
        icon: 'solar:graph-up-linear',
        opensOwnPage: true,
        tint: 'accent',
    },
    {
        id: 'accountingPeriods',
        area: 'accounting',
        icon: 'solar:calendar-linear',
        opensOwnPage: true,
        tint: 'accent',
    },
    {
        id: 'accountingSettings',
        area: 'accounting',
        icon: 'solar:settings-linear',
        opensOwnPage: true,
        tint: 'accent',
    },
    // People and system
    {
        id: 'addEmployee',
        area: 'peopleAndSystem',
        icon: 'solar:user-plus-linear',
        opensOwnPage: false,
        tint: 'accent',
    },
    {
        id: 'addUser',
        area: 'peopleAndSystem',
        icon: 'solar:users-group-rounded-linear',
        opensOwnPage: false,
        tint: 'accent',
    },
];

/**
 * What the page opens with until someone curates their own six.
 *
 * The daily writes, one per area that has one. Five of the previous six named
 * actions that no longer exist; an unknown id is dropped silently, so a stale
 * default does not fail — it just renders fewer tiles than the grid expects.
 */
export const DEFAULT_PINNED_IDS = [
    'addProduct',
    'newPurchase',
    'addSupplier',
    'recordOperation',
    'addCategory',
    'addEmployee',
];

/**
 * Seeded rather than empty. Recent is the page teaching its own shortcuts, and
 * a row that is blank on a first visit teaches nothing; these give way to real
 * use as soon as an action is run.
 */
export const DEFAULT_RECENT_IDS = [
    'productList',
    'addBranch',
    'accountingJournal',
    'addCategory',
];

/** How many chips the Recent row keeps. Beyond four it stops being a glance. */
export const RECENT_LIMIT = 4;

export const ACTION_BY_ID: Record<string, ControlAction | undefined> =
    Object.fromEntries(CONTROL_ACTIONS.map((action) => [action.id, action]));

export const TOTAL_ACTION_COUNT = CONTROL_ACTIONS.length;

/** Icon-chip classes per tint — the soft surface plus its foreground. */
export const TINT_CHIP_CLASS: Record<ActionTint, string> = {
    accent: 'bg-accent/12 text-accent',
    success: 'bg-success/15 text-success',
    danger: 'bg-danger/12 text-danger',
};

/**
 * The subtitle under a pinned tile: where the action opens.
 *
 * Two rows used to carry a mock figure instead — a bank balance and a count of
 * waybill drafts, both hardcoded. They went with the actions they belonged to
 * (BOS-181). When a tile can show something it has actually read, this is where
 * that goes; inventing one was worse than saying where the press leads.
 */
export type PinnedDetail = {
    key: string;
    params?: Record<string, string | number>;
};

export const pinnedDetail = (action: ControlAction): PinnedDetail =>
    action.opensOwnPage
        ? { key: 'controlCenter.details.opensOwnPage' }
        : { key: 'controlCenter.details.opensHere' };

export type AreaSection = {
    area: ActionArea;
    actions: ControlAction[];
};

/** The catalog, grouped into its display sections. Empty areas are dropped. */
export const groupByArea = (actions: ControlAction[]): AreaSection[] =>
    AREA_ORDER.map((area) => ({
        area,
        actions: actions.filter((action) => action.area === area),
    })).filter((section) => section.actions.length > 0);

/**
 * Derived, not written down. The catalog header says how many areas it spans,
 * and a hand-kept number there is one that goes stale the first time an action
 * moves.
 */
export const AREA_COUNT = groupByArea(CONTROL_ACTIONS).length;

/**
 * A palette row: an action plus whether this session can run it.
 *
 * A refused match is returned, muted and unselectable, rather than filtered
 * out — a search that silently omits a row the reader is not allowed reads as
 * "there is no such thing", which is a worse answer than "not for you".
 */
export type PaletteResult =
    | { kind: 'available'; action: ControlAction; label: string }
    | { kind: 'refused'; action: ControlAction; label: string };

/**
 * How the caller resolves the labels, and whether a session may run a row.
 *
 * A bag rather than four positional arguments: the palette already passed
 * three, and the fourth is the one whose omission is silent — a missing
 * `isRefused` reads as "everything is allowed" and lets the keyboard run
 * something the server would refuse.
 */
export type PaletteResolvers = {
    labelOf: (id: string) => string;
    areaLabelOf: (area: ActionArea) => string;
    /**
     * Whether this session is refused the action.
     *
     * The gate lives in the surface registry, not here: this fixture stays a
     * description of the product, and who may do what is a fact about the
     * feature and the session.
     */
    isRefused: (id: string) => boolean;
};

/**
 * Search names **and** area names, so "bank" finds every row under Bank and
 * payments and not only the two whose names start with it.
 *
 * Labels are resolved by the caller: matching happens against what the reader
 * can see, which in this app is one of two languages.
 *
 * **A refused match is shown, not run.** The palette has nowhere to anchor a
 * refusal popover — its rows are `role="option"` in a listbox, and running one
 * closes the list the row lived in — so a refused action is present, muted, and
 * unreachable by Enter. The alternative, letting it through, opens a form whose
 * every write would 403.
 */
export const searchActions = (
    query: string,
    { labelOf, areaLabelOf, isRefused }: PaletteResolvers,
): PaletteResult[] => {
    const needle = query.trim().toLowerCase();
    if (needle.length === 0) {
        return [];
    }

    const matches = (label: string, area: ActionArea): boolean =>
        label.toLowerCase().includes(needle) ||
        areaLabelOf(area).toLowerCase().includes(needle);

    const available = CONTROL_ACTIONS.filter((action) =>
        matches(labelOf(action.id), action.area),
    ).map<PaletteResult>((action) => ({
        kind: isRefused(action.id) ? 'refused' : 'available',
        action,
        label: labelOf(action.id),
    }));

    // Runnable first, always: the muted rows are context, not candidates.
    const runnable = available.filter((row) => row.kind === 'available');
    const refused = available.filter((row) => row.kind === 'refused');
    return [...runnable, ...refused];
};

/** The rows a keyboard may land on — the available ones, in list order. */
export const selectableResults = (results: PaletteResult[]): ControlAction[] =>
    results.flatMap((result) =>
        result.kind === 'available' ? [result.action] : [],
    );

/** Index of `index` moved by `delta`, wrapping at both ends. */
export const moveHighlight = (
    index: number,
    delta: number,
    count: number,
): number => {
    if (count === 0) {
        return 0;
    }
    return (index + delta + count) % count;
};

/** Addressable by `aria-activedescendant`, and by the scroll-into-view. */
export const paletteRowElementId = (id: string): string =>
    `control-center-palette-row-${id}`;
